/**
 * Display formatting for the instructor dashboard.
 */

// Period vocabulary: the four keys every staff dashboard endpoint reads
// through the backend's student_activity.parse_period (the tracker
// dashboard, /teacher-analytics/overview and /leaderboard, and
// /organisations/students), sent as `period` to all of them. `today` is
// since UTC midnight and `all` has no lower bound. The UI must not offer a
// fifth.
const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'Since account opened' },
];

const DEFAULT_PERIOD = '30d';

const isPeriod = (value) => PERIOD_OPTIONS.some((o) => o.value === value);

// The caption for anything not filtered by the period: the Overview's
// Inactive, Never started, No documents and documents on file.
const LIFETIME_CAPTION = 'Since each account opened';

const CAPTIONS = {
  today: 'Today',
  '7d': 'In the last 7 days',
  '30d': 'In the last 30 days',
  all: LIFETIME_CAPTION,
};

/**
 * The caption under a count over a period key, so "all" reads as what it
 * is rather than "In the last since account opened". Empty for anything
 * that is not one of the four keys.
 */
const windowCaption = (period) => CAPTIONS[period] || '';

/** "1 item" / "12 items". `singular` relabels the unit, e.g. "Session". */
const itemCount = (n, singular = 'item') => {
  const count = Number(n) || 0;
  const word = count === 1 ? singular : `${singular}s`;
  return `${count.toLocaleString()} ${word}`;
};

/** "3 days ago" / "Today" / "Never". days_ago is null when never active. */
const relativeDays = (daysAgo) => {
  if (daysAgo == null) return 'Never';
  const n = Number(daysAgo);
  if (!Number.isFinite(n)) return 'Never';
  if (n <= 0) return 'Today';
  if (n === 1) return 'Yesterday';
  return `${n} days ago`;
};

/**
 * Whole calendar days from an ISO timestamp to `now`, in the viewer's own
 * timezone, so "Today" means today on the viewer's clock rather than within
 * 24 hours. Null for a missing or unparseable timestamp (never active).
 */
const daysSince = (isoTimestamp, now = new Date()) => {
  if (!isoTimestamp) return null;
  const then = new Date(isoTimestamp);
  if (Number.isNaN(then.getTime())) return null;
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOfDay(now) - startOfDay(then)) / 86400000);
};

/** Em-dash for absent values, so "no data" never reads as a measured zero. */
const EM_DASH = '—';

const orDash = (value) => (value == null || value === '' ? EM_DASH : value);


/** "14 Jul" — compact axis tick. Parsed as UTC so the date never shifts. */
const shortDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(`${String(dateStr).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return `${d.getUTCDate()} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getUTCMonth()]}`;
};

/** "14 July 2026" — full date for tooltips. */
const longDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(`${String(dateStr).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

/**
 * Pick at most `max` evenly spaced indices to label, always including the first
 * and last, so a long window does not produce unreadable overlapping ticks.
 */
const axisTickIndices = (length, max = 6) => {
  if (length <= 0) return [];
  if (length <= max) return Array.from({ length }, (_, i) => i);
  const step = (length - 1) / (max - 1);
  const out = [];
  for (let i = 0; i < max; i += 1) out.push(Math.round(i * step));
  return [...new Set(out)];
};

export {
  windowCaption,
  isPeriod,
  DEFAULT_PERIOD,
  LIFETIME_CAPTION,
  itemCount,
  shortDate,
  longDate,
  axisTickIndices,
  relativeDays,
  daysSince,
  orDash,
  PERIOD_OPTIONS,
  EM_DASH,
};
